/**
 * Unified left-rail plan list used in both "no plan open" and "plan open" modes.
 * Eliminates the duplicated search + list + header blocks from the original workspace.
 */
import { Panel as ResizablePanel } from 'react-resizable-panels'
import { PlanCard } from './PlanCard'
import type { ConstructorPlan } from './constructorTypes'

export function ConstructorPlanListRail({
  plans,
  loading,
  selectedPlanId,
  search,
  activePatientId,
  activePatientName,
  panelId,
  defaultSize,
  minSize,
  maxSize,
  collapsible,
  onSelectPlan,
  onCreatePlan,
  onSearchChange,
  headerExtra,
}: {
  plans: ConstructorPlan[]
  loading: boolean
  selectedPlanId: number | null
  search: string
  activePatientId: number | null
  activePatientName: string
  panelId: string
  defaultSize: string
  minSize: string
  maxSize?: string
  collapsible?: boolean
  onSelectPlan: (planId: number) => void
  onCreatePlan: () => void
  onSearchChange: (value: string) => void
  /** Extra content rendered above the list header (e.g. breadcrumb). */
  headerExtra?: React.ReactNode
}) {
  const headingText = activePatientId !== null
    ? (<>Планы пациента <span className="text-slate-400">({plans.length})</span></>)
    : (<>Планы конструктора <span className="text-slate-400">({plans.length})</span></>)

  return (
    <ResizablePanel
      id={panelId}
      defaultSize={defaultSize}
      minSize={minSize}
      maxSize={maxSize}
      collapsible={collapsible}
      className="flex min-h-0 flex-col"
    >
      {headerExtra}

      <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-2.5 py-1.5">
        <h3 className="flex-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-600">
          {headingText}
        </h3>
        <button
          type="button"
          className="rounded border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700 hover:bg-blue-100"
          onClick={onCreatePlan}
          title="Создать новый план"
        >
          + Новый
        </button>
      </div>

      {activePatientId !== null && activePatientName !== '' && (
        <div className="bg-blue-50 px-2.5 py-1 text-[10px] text-blue-700">
          Только планы «{activePatientName}»
        </div>
      )}

      <div className="border-b border-slate-200 px-2 py-1.5">
        <input
          className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs placeholder-slate-400 focus:border-blue-400 focus:outline-none"
          placeholder="Поиск по плану / пациенту..."
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {loading ? (
          <p className="py-6 text-center text-xs text-slate-400">Загрузка планов...</p>
        ) : plans.length === 0 ? (
          activePatientId !== null ? (
            <p className="py-6 text-center text-xs text-slate-400">
              У этого пациента ещё нет планов конструктора
            </p>
          ) : (
            <div className="py-10 text-center">
              <p className="text-sm text-slate-500">Пока нет планов конструктора</p>
              <p className="mt-1 text-[11px] text-slate-400">Создайте первый план — выберите пациента и приступайте.</p>
              <button
                type="button"
                onClick={onCreatePlan}
                className="mt-3 inline-flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
              >
                + Создать план
              </button>
            </div>
          )
        ) : (
          <div>
            {plans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                selected={plan.id === selectedPlanId}
                onClick={() => onSelectPlan(plan.id)}
              />
            ))}
          </div>
        )}
      </div>
    </ResizablePanel>
  )
}
